import type {
	TomConversation,
	TomConversationPropertyContext,
	TomGenerationDiagnostics,
	TomMessage,
	TomMessageFailure,
	TomMessagePresentation,
	TomPropertySnapshot,
	TomMessageSource,
	TomStreamEvent,
} from '../types';
import { TomApiError } from './tomApiError';

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isMessageFailure = (value: unknown): value is TomMessageFailure =>
	isRecord(value) &&
	typeof value.code === 'string' &&
	typeof value.message === 'string' &&
	typeof value.retryable === 'boolean';

const isTomMessageSource = (value: unknown): value is TomMessageSource =>
	isRecord(value) &&
	typeof value.id === 'string' &&
	typeof value.title === 'string' &&
	(value.files === undefined ||
		(Array.isArray(value.files) &&
			value.files.every(
				file =>
					isRecord(file) &&
					typeof file.id === 'number' &&
					typeof file.name === 'string',
			))) &&
	((typeof value.source === 'string' &&
		typeof value.topicName === 'string') ||
		typeof value.detail === 'string');

const isNullableNumber = (value: unknown) =>
	value === null || typeof value === 'number';

const isNullableString = (value: unknown) =>
	value === null || typeof value === 'string';

const isTomPropertySnapshot = (value: unknown): value is TomPropertySnapshot =>
	isRecord(value) &&
	(value.propertyId === null || typeof value.propertyId === 'number') &&
	typeof value.address === 'string' &&
	typeof value.stateName === 'string' &&
	typeof value.stateCode === 'string' &&
	typeof value.countyName === 'string' &&
	typeof value.cityName === 'string' &&
	isNullableNumber(value.listPrice) &&
	isNullableNumber(value.bedroomsCount) &&
	isNullableNumber(value.bathroomsCount) &&
	isNullableNumber(value.squareFeet) &&
	isNullableNumber(value.propertyTaxes) &&
	isNullableNumber(value.mortgage) &&
	isNullableNumber(value.cashFlow) &&
	isNullableNumber(value.fmr) &&
	isNullableString(value.imageUrl) &&
	isNullableString(value.lastUpdatedAt);

const isTomMessagePresentation = (
	value: unknown,
): value is TomMessagePresentation => {
	if (!isRecord(value) || value.version !== 1) {
		return false;
	}

	const hasValidContract =
		(value.kind === 'property_search_results' &&
			(value.status === 'awaiting_confirmation' ||
				value.status === null)) ||
		(value.kind === 'property_discovery_results' && value.status === null);

	return (
		hasValidContract &&
		Array.isArray(value.properties) &&
		value.properties.length > 0 &&
		value.properties.every(isTomPropertySnapshot)
	);
};

const isTomConversationPropertyContext = (
	value: unknown,
): value is TomConversationPropertyContext =>
	isRecord(value) &&
	(value.source === 'user_attached' ||
		value.source === 'assistant_resolved') &&
	isTomPropertySnapshot(value.property);

const isTomMessage = (value: unknown): value is TomMessage =>
	isRecord(value) &&
	typeof value.id === 'number' &&
	typeof value.conversationId === 'number' &&
	(value.replyToMessageId === null ||
		typeof value.replyToMessageId === 'number') &&
	(value.role === 'user' || value.role === 'assistant') &&
	['pending', 'streaming', 'completed', 'failed', 'cancelled'].includes(
		String(value.status),
	) &&
	(value.content === null || typeof value.content === 'string') &&
	(value.sources === undefined ||
		(Array.isArray(value.sources) &&
			value.sources.every(isTomMessageSource))) &&
	(value.presentation === undefined ||
		value.presentation === null ||
		isTomMessagePresentation(value.presentation)) &&
	(value.failure === null || isMessageFailure(value.failure)) &&
	typeof value.createdAt === 'string' &&
	(value.completedAt === null || typeof value.completedAt === 'string');

const isConversation = (value: unknown): value is TomConversation =>
	isRecord(value) &&
	typeof value.id === 'number' &&
	typeof value.title === 'string' &&
	(value.status === 'active' || value.status === 'archived') &&
	(value.propertyContext === undefined ||
		value.propertyContext === null ||
		isTomConversationPropertyContext(value.propertyContext)) &&
	typeof value.createdAt === 'string' &&
	typeof value.updatedAt === 'string';

const isOptionalRecord = (
	value: unknown,
): value is Record<string, unknown> | null | undefined =>
	value === undefined || value === null || isRecord(value);

const isTomDiagnosticModelCall = (value: unknown) =>
	isRecord(value) &&
	typeof value.sequence === 'number' &&
	typeof value.callKind === 'string' &&
	typeof value.requestedModel === 'string' &&
	typeof value.succeeded === 'boolean';

const isTomToolCallDiagnostic = (value: unknown) =>
	isRecord(value) &&
	typeof value.startedAt === 'string' &&
	typeof value.durationMilliseconds === 'number' &&
	typeof value.success === 'boolean' &&
	typeof value.summary === 'string' &&
	Array.isArray(value.groundedSources) &&
	typeof value.requiresGroundingManifest === 'boolean';

const isTomDiagnosticException = (value: unknown) =>
	value === undefined ||
	value === null ||
	(isRecord(value) &&
		typeof value.type === 'string' &&
		typeof value.message === 'string' &&
		typeof value.fullException === 'string');

const isTomGenerationDiagnostics = (
	value: unknown,
): value is TomGenerationDiagnostics => {
	if (
		!isRecord(value) ||
		!isRecord(value.request) ||
		!isRecord(value.response)
	) {
		return false;
	}

	const request = value.request;
	const response = value.response;
	const generation = value.generation;
	const agentResult = value.agentResult;
	return (
		typeof value.capturedAt === 'string' &&
		typeof value.traceId === 'string' &&
		typeof request.method === 'string' &&
		typeof request.path === 'string' &&
		typeof request.conversationId === 'number' &&
		typeof request.userId === 'number' &&
		typeof request.userMessageId === 'number' &&
		typeof request.assistantMessageId === 'number' &&
		typeof request.generationRequestId === 'number' &&
		typeof request.content === 'string' &&
		(response.terminalEvent === 'completed' ||
			response.terminalEvent === 'error') &&
		isTomMessage(response.assistantMessage) &&
		(response.error === undefined ||
			response.error === null ||
			isMessageFailure(response.error)) &&
		isTomDiagnosticException(value.exception) &&
		isOptionalRecord(agentResult) &&
		(agentResult === undefined ||
			agentResult === null ||
			typeof agentResult.model === 'string') &&
		isOptionalRecord(generation) &&
		(generation === undefined ||
			generation === null ||
			((generation.primaryModel === undefined ||
				generation.primaryModel === null ||
				typeof generation.primaryModel === 'string') &&
				(generation.responseDurationMilliseconds === undefined ||
					generation.responseDurationMilliseconds === null ||
					typeof generation.responseDurationMilliseconds ===
						'number'))) &&
		Array.isArray(value.modelCalls) &&
		value.modelCalls.every(isTomDiagnosticModelCall) &&
		Array.isArray(value.toolCalls) &&
		value.toolCalls.every(isTomToolCallDiagnostic) &&
		(value.collectionError === undefined ||
			value.collectionError === null ||
			typeof value.collectionError === 'string')
	);
};

const isDiagnosticsForAssistantMessage = (
	value: unknown,
	assistantMessageId: number,
) =>
	value === undefined ||
	(isTomGenerationDiagnostics(value) &&
		value.response.assistantMessage.id === assistantMessageId);

const parseStreamEvent = (line: string): TomStreamEvent | null => {
	const trimmed = line.trim();
	if (!trimmed) {
		return null;
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(trimmed);
	} catch {
		throw new TomApiError('Tom AI returned an invalid streaming response.');
	}

	if (!isRecord(parsed) || typeof parsed.type !== 'string') {
		throw new TomApiError('Tom AI returned an invalid streaming event.');
	}

	switch (parsed.type) {
		case 'accepted':
			if (
				isTomMessage(parsed.assistantMessage) &&
				(parsed.userMessage === undefined ||
					isTomMessage(parsed.userMessage))
			) {
				return parsed as TomStreamEvent;
			}
			break;
		case 'progress':
			if (
				typeof parsed.assistantMessageId === 'number' &&
				typeof parsed.stage === 'string' &&
				typeof parsed.label === 'string'
			) {
				return parsed as TomStreamEvent;
			}
			break;
		case 'delta':
			if (
				typeof parsed.assistantMessageId === 'number' &&
				typeof parsed.content === 'string'
			) {
				return parsed as TomStreamEvent;
			}
			break;
		case 'completed':
			if (
				isTomMessage(parsed.assistantMessage) &&
				isConversation(parsed.conversation) &&
				isDiagnosticsForAssistantMessage(
					parsed.diagnostics,
					parsed.assistantMessage.id,
				)
			) {
				return parsed as TomStreamEvent;
			}
			break;
		case 'error':
			if (
				isTomMessage(parsed.assistantMessage) &&
				isMessageFailure(parsed.error) &&
				isDiagnosticsForAssistantMessage(
					parsed.diagnostics,
					parsed.assistantMessage.id,
				)
			) {
				return parsed as TomStreamEvent;
			}
			break;
		default:
			throw new TomApiError(
				`Tom AI returned an unsupported stream event: ${parsed.type}.`,
			);
	}

	throw new TomApiError('Tom AI returned an invalid streaming event.');
};

export const readTomStreamText = async (stream: ReadableStream<Uint8Array>) => {
	const reader = stream.getReader();
	const decoder = new TextDecoder();
	let text = '';
	let isDone = false;

	try {
		while (!isDone) {
			const { done, value } = await reader.read();
			isDone = done;
			if (value) {
				text += decoder.decode(value, { stream: !done });
			}
		}
		text += decoder.decode();
	} finally {
		reader.releaseLock();
	}

	return text;
};

export const consumeTomStream = async (
	stream: ReadableStream<Uint8Array>,
	onEvent: (event: TomStreamEvent) => void,
) => {
	const reader = stream.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let receivedTerminalEvent = false;
	let isDone = false;

	const handleLine = (line: string) => {
		const event = parseStreamEvent(line);
		if (!event) {
			return;
		}

		if (event.type === 'completed' || event.type === 'error') {
			receivedTerminalEvent = true;
		}

		onEvent(event);
	};

	try {
		while (!isDone) {
			const { done, value } = await reader.read();
			isDone = done;
			if (value) {
				buffer += decoder.decode(value, { stream: !done });
				const lines = buffer.split('\n');
				buffer = lines.pop() ?? '';
				lines.forEach(handleLine);
			}
		}

		buffer += decoder.decode();
		handleLine(buffer);
	} finally {
		reader.releaseLock();
	}

	if (!receivedTerminalEvent) {
		throw new TomApiError(
			'Tom AI response ended before the request was completed.',
		);
	}
};
